import { useDocumentMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useDocumentMetadataUserEdit'
import { useDocumentMetadataUserEditSaveActions } from 'features/knowledgeDocumentViewer/hooks/useDocumentMetadataUserEditSaveActions'
import { useDocumentPageMetadataUserEdit } from 'features/knowledgeDocumentViewer/hooks/useDocumentPageMetadataUserEdit'
import { usePageMetadataUserEditSaveActions } from 'features/knowledgeDocumentViewer/hooks/usePageMetadataUserEditSaveActions'
import { useSystemDocumentPageByNum } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentPageByNum'
import type {
  DocumentMetadataUserEditEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import type { FamilyDocumentMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyDocumentMetadataUserEditPuts'
import type { FamilyPageMetadataSaveMember } from 'features/knowledgeDocumentViewer/utils/buildFamilyPageMetadataUserEditPuts'
import { useCallback, useEffect, useState } from 'react'

export type StackedMetadataEditMode = 'none' | 'document' | 'page'

export type UseStackedMetadataEditControllersArgs = {
  bqDocumentId: string | undefined
  systemDocumentEntryId: string | undefined
  systemDocument: SystemDocumentEntry | undefined
  pageNum: number
  userEditsEnabled: boolean
  pageDetailsEnabled: boolean
  resetKey?: string
  onEditingChange?: (isEditing: boolean) => void
}

export type UseStackedMetadataEditControllersResult = {
  metadataEditMode: StackedMetadataEditMode
  showDocumentDetails: boolean
  showPageDetails: boolean
  isEditingMetadata: boolean
  documentUserEdit: DocumentMetadataUserEditEntry | undefined
  isDocumentUserEditLoading: boolean
  pageEntry: SystemDocumentPageEntry | undefined
  isPageLoading: boolean
  pageUserEdit: DocumentPageMetadataUserEditEntry | undefined
  pageUserEdits: DocumentPageMetadataUserEditEntry[]
  isPageUserEditLoading: boolean
  mutatePageUserEdit: () => Promise<unknown>
  isSavingDocumentEdit: boolean
  saveDocumentMetadata: (
    values: DocumentMetadataUserEditValues
  ) => Promise<void>
  saveDocumentMetadataFamily: (
    values: DocumentMetadataUserEditValues,
    members: readonly FamilyDocumentMetadataSaveMember[]
  ) => Promise<void>
  isSavingPageEdit: boolean
  savePageMetadata: (
    values: DocumentPageMetadataUserEditValues
  ) => Promise<void>
  savePageMetadataFamily: (
    values: DocumentPageMetadataUserEditValues,
    members: readonly FamilyPageMetadataSaveMember[]
  ) => Promise<void>
  handleDocumentEditingChange: (isEditing: boolean) => void
  handlePageEditingChange: (isEditing: boolean) => void
}

/** Shared stacked Document/Page Details load, save, and exclusive edit mode. */
export function useStackedMetadataEditControllers({
  bqDocumentId,
  systemDocumentEntryId,
  systemDocument,
  pageNum,
  userEditsEnabled,
  pageDetailsEnabled,
  resetKey,
  onEditingChange,
}: UseStackedMetadataEditControllersArgs): UseStackedMetadataEditControllersResult {
  const [metadataEditMode, setMetadataEditMode] =
    useState<StackedMetadataEditMode>('none')

  useEffect(() => {
    setMetadataEditMode('none')
  }, [resetKey])

  const {
    edit: documentUserEdit,
    isLoading: isDocumentUserEditLoading,
    mutate: mutateDocumentUserEdit,
  } = useDocumentMetadataUserEdit({
    bqDocumentId,
    enabled: userEditsEnabled,
  })
  const { page: pageEntry, isLoading: isPageLoading } =
    useSystemDocumentPageByNum({
      systemDocumentEntryId,
      bqDocumentId,
      pageNum,
      enabled: pageDetailsEnabled,
    })
  const {
    edit: pageUserEdit,
    edits: pageUserEdits,
    isLoading: isPageUserEditLoading,
    mutate: mutatePageUserEdit,
  } = useDocumentPageMetadataUserEdit({
    bqDocumentId,
    pageNum,
    enabled: pageDetailsEnabled,
  })
  const {
    isSaving: isSavingDocumentEdit,
    saveDocumentMetadata,
    saveDocumentMetadataFamily,
  } = useDocumentMetadataUserEditSaveActions({
    bqDocumentId,
    systemDocument,
    documentUserEdit,
    mutateDocumentUserEdit,
    pageNum,
    pageEntry,
    pageUserEdit,
    mutatePageUserEdit,
  })
  const {
    isSaving: isSavingPageEdit,
    savePageMetadata,
    savePageMetadataFamily,
  } = usePageMetadataUserEditSaveActions({
    bqDocumentId,
    pageNum,
    pageEntry,
    pageUserEdit,
    mutatePageUserEdit,
    systemDocument,
    documentUserEdit,
    mutateDocumentUserEdit,
  })

  const setMetadataEditModeAndNotify = useCallback(
    (mode: StackedMetadataEditMode) => {
      setMetadataEditMode(mode)
      onEditingChange?.(mode !== 'none')
    },
    [onEditingChange]
  )

  const handleDocumentEditingChange = useCallback(
    (isEditing: boolean) => {
      setMetadataEditModeAndNotify(isEditing ? 'document' : 'none')
    },
    [setMetadataEditModeAndNotify]
  )

  const handlePageEditingChange = useCallback(
    (isEditing: boolean) => {
      setMetadataEditModeAndNotify(isEditing ? 'page' : 'none')
    },
    [setMetadataEditModeAndNotify]
  )

  return {
    metadataEditMode,
    showDocumentDetails: metadataEditMode !== 'page',
    showPageDetails: pageDetailsEnabled && metadataEditMode !== 'document',
    isEditingMetadata: metadataEditMode !== 'none',
    documentUserEdit,
    isDocumentUserEditLoading,
    pageEntry,
    isPageLoading,
    pageUserEdit,
    pageUserEdits,
    isPageUserEditLoading,
    mutatePageUserEdit,
    isSavingDocumentEdit,
    saveDocumentMetadata,
    saveDocumentMetadataFamily,
    isSavingPageEdit,
    savePageMetadata,
    savePageMetadataFamily,
    handleDocumentEditingChange,
    handlePageEditingChange,
  }
}
